import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';

import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ORIGINATION_SUPPORT_DISPLAY, ORIGINATION_SUPPORT_TEL } from '@ld.consumer-portal/constants';
import { DashboardService, MlaAuthService, MlaBorrowerService, MlaConfigService, MlaDashboardService } from '@ld.consumer-portal/data-access';
import {
  heroiconsChatBubbleBottomCenterText,
  heroiconsCheckCircle,
  heroiconsChevronDown,
  heroiconsChevronLeft,
  heroiconsEnvelope,
  heroiconsPhone,
  heroiconsQuestionMarkCircle,
  heroiconsShieldCheck,
  heroiconsUserCircle,
  logoutIcon,
  tourPortalIconDark,
  userIconDark,
} from '@ld.consumer-portal/data-uri';
import { MfaPreferencesModalComponent } from '@ld.consumer-portal/modals';
import { cachedOriginationBorrowerName, ENVIRONMENT, originationApplications, OriginationBorrower, renderPhoneNumber, resolveDisplayBorrowerName, setCachedOriginationBorrowerName, setOriginationApplications, toTitleCase } from '@ld.consumer-portal/models';
import { ModalQueueService } from '@ld.consumer-portal/routing';
import { AnalyticsService, AuthService, BorrowerSwitchingService, BrowserService, LoanSelectionService, PlatformService, RemoteConfigService, ThemeService, TourGuideService, TourPreferenceService } from '@ld.consumer-portal/utils';
import { filter, map } from 'rxjs/operators';
import { NativeMenuService } from '../native-bottom-sheet/native-menu.service';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

export type NativeHeaderVariant = 'dashboard' | 'loan-detail';

@Component({
  selector: 'ld-global-header',
  imports: [MatDividerModule, MatIconModule, MatMenuModule, RouterLink, SvgIconComponent, TranslocoDirective, MfaPreferencesModalComponent],
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sticky top-0 z-50' },
})
export class GlobalHeaderComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private authService = inject(AuthService);
  private tourService = inject(TourGuideService);
  private tourPreferenceService = inject(TourPreferenceService);
  private env = inject(ENVIRONMENT);
  private borrowerSwitchingService = inject(BorrowerSwitchingService);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  private themeService = inject(ThemeService);
  private platformService = inject(PlatformService);
  protected readonly nativeMenuService = inject(NativeMenuService);
  protected readonly mlaConfigService = inject(MlaConfigService);
  private readonly dashboardService = inject(DashboardService);
  private readonly modalQueueService = inject(ModalQueueService);
  private readonly mlaDashboardService = inject(MlaDashboardService);
  private readonly mlaAuthService = inject(MlaAuthService);
  private readonly mlaBorrowerService = inject(MlaBorrowerService);
  private browserService = inject(BrowserService);
  private router = inject(Router);
  private loanSelectionService = inject(LoanSelectionService);
  private readonly remoteConfigService = inject(RemoteConfigService);

  // Mirrors the dashboard gate: on native, origination stays hidden until the
  // app-wide remote flag is flipped on. Web never consults the flag.
  private readonly originationHiddenOnNative = computed(
    () => this.isNativeMobile() && !this.remoteConfigService.config().show_origination,
  );

  private readonly hasPendingOriginationApp = computed(() =>
    !this.originationHiddenOnNative() &&
    originationApplications().some(
      (app) => app.status === 'PendingApproval' && (app.loanType === 'Purchase' || app.loanType === 'Refinance'),
    ),
  );

  protected readonly showTourOption = computed(
    () =>
      !this.themeService.isJvThemeActive() &&
      (this.loanSelectionService.hasMortgageOrHeloc() || this.hasPendingOriginationApp()),
  );

  readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 767px)') },
  );

  readonly isMobile = computed(() => this.isNativeMobile() || this.isMobileViewport());

  private readonly defaultLogo = '/assets/images/loandepot-logo-3d-dark-metallic.svg';

  readonly logo = computed(() => {
    if (this.themeService.isJvThemeActive()) {
      return this.themeService.getLogoUrl();
    }
    return this.defaultLogo;
  });

  readonly isJvTheme = computed(() => this.themeService.isJvThemeActive());

  readonly partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  readonly userIconDark = userIconDark;
  readonly logoutIcon = logoutIcon;

  protected readonly userCircleIcon = heroiconsUserCircle;
  protected readonly chevronLeftIcon = heroiconsChevronLeft;
  protected readonly helpIcon = heroiconsQuestionMarkCircle;
  protected readonly envelopeIcon = heroiconsEnvelope;
  protected readonly checkCircleIcon = heroiconsCheckCircle;
  protected readonly feedbackIcon = heroiconsChatBubbleBottomCenterText;
  protected readonly shieldCheckIcon = heroiconsShieldCheck;
  protected readonly chevronDownIcon = heroiconsChevronDown;
  protected readonly phoneIcon = heroiconsPhone;
  protected readonly tourIcon = tourPortalIconDark;

  protected readonly contactSupportPhone = renderPhoneNumber(ORIGINATION_SUPPORT_DISPLAY);
  protected readonly contactSupportTel = ORIGINATION_SUPPORT_TEL;

  private readonly currentUrl = signal(this.router.url);

  protected readonly showContactSupport = computed(() => {
    if (this.forceShowContactSupport()) {
      return true;
    }
    const url = this.currentUrl();
    return url.startsWith('/origination/');
  });

  // Only prefer the origination borrower/co-borrower while actually on an origination route.
  // On the dashboard the header must resolve the name exactly like the welcome banner (the
  // servicing borrower wins), so it stays consistent across navigation and tab-visibility
  // refreshes instead of leaking a co-borrower from a prior origination selection.
  private readonly onOriginationRoute = computed(() => {
    const url = this.currentUrl();
    return url.startsWith('/origination/') || url.startsWith('/apply/');
  });

  readonly currentBorrower = this.borrowerSwitchingService.currentBorrower;

  readonly availableBorrowers = this.borrowerSwitchingService.availableBorrowers;

  // Title-case names so the desktop switcher matches the mobile bottom-sheet list.
  protected readonly borrowerOptions = computed(() =>
    this.availableBorrowers().map((borrower) => ({
      ...borrower,
      displayName: toTitleCase(`${borrower.firstName ?? ''} ${borrower.lastName ?? ''}`.trim()),
    })),
  );

  userName = computed(() => {
    // Read inline (like the welcome banner) so this recomputes with the other name inputs.
    const cached = this.mlaBorrowerService.getCachedPrimaryBorrower();
    return resolveDisplayBorrowerName({
      // Retained (last non-empty) snapshot, so a background refresh that purges the live
      // loan set does not drop the servicing borrower and swap the name to a fallback.
      loanResponses: this.dashboardService.nameSourceLoanResponses(),
      // Servicing shows the latest loan's coBorrower when the logged-in user's email
      // matches theirs; otherwise the loan's primary borrower.
      currentUserEmail: this.authService.getUserEmail(),
      originationName: this.originationDisplayName(),
      // On origination routes the selected application's borrower/co-borrower supersedes any
      // servicing loan name; on the dashboard this is false so the servicing borrower wins,
      // matching the welcome banner.
      preferOrigination: this.onOriginationRoute(),
      // Same primary-borrower fallback the welcome banner uses, so when there is no servicing
      // name the dashboard header resolves the primary borrower instead of a stale origination
      // co-borrower selection.
      cachedBorrowerName: cached ? `${cached.firstName ?? ''} ${cached.lastName ?? ''}`.trim() : null,
      authName: this.authService.userName(),
      defaultName: 'Homeowner',
    });
  });

  // Cache each resolved origination name so it can be reused while applications reload.
  private readonly cacheOriginationNameEffect = effect(() => {
    setCachedOriginationBorrowerName(this.mlaDashboardService.liveOriginationBorrowerName());
  });

  private readonly originationDisplayName = computed(() => {
    // Honor an explicit borrower switch (co-borrower) before falling back to the
    // lookup keyed off the selected (or latest) origination application.
    const switchedBorrower = this.currentBorrower();
    const switchedName = switchedBorrower
      ? `${switchedBorrower.firstName ?? ''} ${switchedBorrower.lastName ?? ''}`.trim()
      : '';
    if (switchedName) return switchedName;

    const liveName = this.mlaDashboardService.liveOriginationBorrowerName();
    if (liveName) return liveName;

    // Cache-independent fallback: the borrower selected during a switch is persisted in
    // sessionStorage, so it survives the dashboard cache being wiped on navigation to
    // origination and avoids a flash of the fallback name while origination data loads.
    // Re-evaluated whenever the cache version bumps (e.g. clearDashboardCache on switch).
    // Only used ON an origination route: on the dashboard this stale selection would surface
    // the co-borrower instead of the servicing/primary borrower shown by the welcome banner.
    if (this.onOriginationRoute()) {
      const selected = this.mlaAuthService.getSelectedBorrowerName();
      const selectedName = selected ? `${selected.firstName ?? ''} ${selected.lastName ?? ''}`.trim() : '';
      if (selectedName) return selectedName;
    }

    // Fall back to the last cached origination name while applications reload.
    return cachedOriginationBorrowerName();
  });

  // The borrower name is only "available" once both GetLoans and origination applications
  // have resolved, since the displayed name is derived from the latest loan/application.
  // Until then we show a loading placeholder instead of briefly rendering the auth-derived
  // full name (which is then superseded), matching the loan card skeletons.
  protected readonly welcomeNameReady = this.dashboardService.userDisplayDataLoaded;

  showBackButton = input<boolean>(false);

  backLabel = input<string>('');

  backClick = output<void>();

  messagesClick = output<void>();

  nativeVariant = input<NativeHeaderVariant>('dashboard');

  welcomeName = input<string>('');

  loanLast4 = input<string>('');

  loanAddress = input<string>('');

  showMessages = input<boolean>(true);

  headerTitle = input<string>('');

  headerSubtitle = input<string>('');

  showHeaderActions = input<boolean>(true);

  showProfile = input<boolean>(true);

  forceShowContactSupport = input<boolean>(false);

  protected onBackClick(): void {
    this.analyticsService.trackMobileHeaderButtonClick('back');
    this.backClick.emit();
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
      });

    effect(() => {
      if (this.themeService.isJvThemeActive()) {
        this.mlaConfigService.getHomeUrl(this.themeService.getCurrentTheme());
      }
    });

    this.loadOriginationApps();
  }

  private async loadOriginationApps(): Promise<void> {
    try {
      const apps = await this.dashboardService.getOriginationApplicationsAsync();
      setOriginationApplications(apps);
    } catch {
      // Non-critical: tour option simply won't appear if load fails
    }
  }

  protected readonly showBorrowerSelector = computed(
    () => this.availableBorrowers().length > 1 && this.borrowerSwitchingService.profileSwitchingAllowed(),
  );

  async onTakeTour(): Promise<void> {
    this.analyticsService.trackMobileTourStarted();
    this.tourPreferenceService.markTourStarted();
    this.modalQueueService.pause();
    this.tourService.setCallbacks(
      () => {
        this.modalQueueService.resume();
      },
      () => {
        this.tourPreferenceService.markTourSkipped();
        this.modalQueueService.resume();
      },
    );
    try {
      const apps = await this.dashboardService.getOriginationApplicationsAsync();
      setOriginationApplications(apps);

      const hasLinkableLoan =
        this.dashboardService.unlinkedLoansFound() && this.dashboardService.unlinkedLoansLookupCompleted();
      if (!this.tourService.prepareTourSteps(this.originationHiddenOnNative() ? [] : apps, hasLinkableLoan)) {
        this.modalQueueService.resume();
        return;
      }

      await this.tourService.startTour();
    } catch {
      this.modalQueueService.resume();
    }
  }

  protected onBorrowerClick(borrower: OriginationBorrower): void {
    const current = this.currentBorrower();
    if (current && borrower.id !== current.id) {
      this.analyticsService.trackMobileBorrowerSwitched(borrower.id);
      this.borrowerSwitchingService.requestBorrowerSwitch(borrower);
    }
  }


  protected onLogoClick(): void {
    const url = this.mlaConfigService.homePageUrl();
    if (url) {
      this.browserService.openUrl(url);
    }
  }

  protected readonly showMfaPreferences = computed(() => !!this.env.appSettings.useCustomerIdentityAuth);

  protected readonly showMfaPreferencesModal = signal(false);

  onUpdateMfaPreferences(): void {
    if (!this.env.appSettings.useCustomerIdentityAuth) {
      return;
    }
    this.analyticsService.trackMobileMfaPreferencesOpened();
    this.showMfaPreferencesModal.set(true);
  }

  protected onMfaPreferencesModalClose(): void {
    this.showMfaPreferencesModal.set(false);
  }

  async onLogout(): Promise<void> {
    await this.authService.logout();
  }

  protected onOpenProfile(): void {
    this.analyticsService.trackMobileHeaderButtonClick('profile');
    this.nativeMenuService.open('profile');
  }

  protected onOpenHelp(): void {
    this.analyticsService.trackMobileHeaderButtonClick('help');
    this.nativeMenuService.open('help');
  }

  protected onMessagesButtonClick(): void {
    this.analyticsService.trackMobileHeaderButtonClick('messages');
    this.messagesClick.emit();
  }
}
